import { abtastyService, cookieService } from '@/services';
import { trackError } from '@utils/error';
import React, { memo, useCallback, useMemo, useRef } from 'react';
import { addToCart } from '../../actions/cart.actions';
import { formatCurrency } from '../../utils/formatters.util';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import AddedToCartModal from '../tailwind/AddedToCartModal';
import { Button, LoadingSpinner } from '../tailwind';
import QuantityInput from '../tailwind/QuantityInput';
import DesignExporter from '../../features/partner/components/DesignExporter';
import { CartItemAddActionTypes } from '@/constants/cart.constants';
import useMiyoFee from '@/hooks/useMiyoFee';
import axios from 'axios';
import CookieNames from '@/constants/cookie-names.constants';
import useVariation from '@hooks/useVariation';
import { HiOutlineBell, HiOutlineShoppingBag } from 'react-icons/hi';

type Props = {
  id: number;
  webImages: any;
  rate: number;
  displayName: string;
  stock: string;
  isGenericProduct?: boolean;
  showPicker?: boolean;
  klaviyoConfigured?: boolean;
  addOns?: any[];
  relatedItems?: any[];
  bundleItems?: any[];
  bundleCustomInfo?: any;
  siteId: string;
  isInlineBundle?: boolean;
  quantity: number;
  setQuantity: (quantity: number) => void;
};

const AddToCart = ({
  id,
  webImages,
  rate: itemRate,
  displayName,
  stock,
  isGenericProduct = false,
  showPicker = false,
  klaviyoConfigured = false,
  addOns,
  relatedItems,
  bundleItems,
  bundleCustomInfo,
  siteId,
  isInlineBundle = false,
  quantity,
  setQuantity,
}: Props) => {
  const dispatch = useAppDispatch();
  const { success, loading } = useSelector((state: any) => state.cartItemAdd);
  const {
    upsell: { selected },
    shopifyId,
  } = useSelector((state: any) => state.pdpItem);

  const isVariant = useVariation('Enhanced PDP');

  const miyoFee = useMiyoFee();
  const rate = itemRate + miyoFee;

  const isBundle = useMemo(() => bundleItems?.length > 0, [bundleItems]);

  const designExporterRef = useRef<DesignExporter>();

  const checkShopifyId = useMemo(() => cookieService.get(CookieNames.SHOPIFY_ENABLED) === 'true', []);

  const decreaseHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (stock !== 'soldOut' && quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const increaseHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (stock !== 'soldOut') {
      setQuantity(quantity + 1);
    }
  };

  // Temporary hide when sold out
  const renderQuantitySection = !isGenericProduct && stock !== 'soldOut' && !loading && !isVariant && (
    <div className='tw:inline-flex tw:min-w-[100px] tw:items-center'>
      <QuantityInput
        value={quantity}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          e.target.value ? setQuantity(parseInt(e.target.value)) : setQuantity(e.target.value as any)
        }
        onBlur={() => !quantity && setQuantity(1)}
        name='quantity'
        disabled={stock === 'soldOut'}
        minQuantity={1}
        decreaseHandler={decreaseHandler}
        increaseHandler={increaseHandler}
        large
      />
    </div>
  );

  const _addToCartSubmit = async (design = null) => {
    const { id: designId, graphicType, printType } = design || {};

    abtastyService.trackClick('PDP: Add to Cart');

    const cartItems = {
      item: {
        id,
        quantity,
        rateFormatted: formatCurrency(rate),
        rate,
        displayName,
        webImages,
        isBundle,
        isInlineBundle,
        isUpsell: false,
        designId,
        graphicType,
        printType,
      },
      upsell: null,
      bundleItems: null,
      addOns: null,
    };

    if (selected.length > 0) {
      cartItems.upsell = selected.map((i) => {
        return {
          baseShopItemId: id,
          id: i.itemId,
          quantity: 1,
          rateFormatted: formatCurrency(i.rate),
          rate: i.rate,
          displayName: i.item.displayName,
          webImages: i.item.webImages,
          isUpsell: true,
          isBundle: false,
          upsellDetails: i,
        };
      });
    }

    if (isBundle) {
      const designs = await Promise.all(
        bundleCustomInfo.designs.map(async (data, i) => {
          const base = i === 0 ? { designId, graphicType, printType } : {};
          if (data?.formData) {
            const { data: designId } = await axios.post('/custom/design/update', data.formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
            return {
              designId,
              graphicType: data.designData.graphicType,
              printType: data.designData.printType,
            };
          } else {
            return base;
          }
        })
      );
      bundleCustomInfo.clear();

      cartItems.bundleItems = bundleItems.map((item, i) => {
        return {
          id: item.id,
          itemId: item.id,
          src: item.webImages.default.thumb.url,
          alt: item.webImages.default.thumb.altText ?? item.displayName,
          ...designs[i],
        };
      });
    }

    if (addOns.length > 0) {
      cartItems.addOns = addOns.map((i) => {
        return {
          id: i.id,
          quantity: cartItems.item.quantity,
          rateFormatted: formatCurrency(i.rateEach),
          displayName: i.displayName,
          webImages: i.webImages,
          isUpsell: false,
          isBundle: false,
          rate: i.rate,
        };
      });
    }

    dispatch(addToCart(cartItems, siteId));
  };

  const addToCartSubmit = () => {
    dispatch({
      type: CartItemAddActionTypes.REQUEST,
    });
    !!designExporterRef.current ? designExporterRef.current.submit() : _addToCartSubmit();
  };

  const renderCTABtn = useCallback(() => {
    if (loading) {
      return (
        <Button type='button' fullWidth='all' size='lg' disabled>
          <LoadingSpinner bgVariant='none' inline white />
          <span className='tw:ml-2'>Adding to cart</span>
        </Button>
      );
    } else if (stock === 'soldOut') {
      <meta itemProp='availability' content='https://schema.org/OutOfStock' />;
      if (klaviyoConfigured && !isBundle) {
        if (isVariant) {
          return (
            <Button
              type='button'
              variant='primary'
              data-bs-toggle='modal'
              data-bs-target='#backInStockSubscribe'
              data-item-id={id}
              data-item-display-name={displayName}
              fullWidth='all'
              size='lg'
              noPadding
              className='tw:h-[64px] tw:rounded-full tw:px-8 tw:py-2 tw:text-base tw:font-normal tw:normal-case'>
              <div className='tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2'>
                <span>Notify Me When In Stock</span>
                <span>
                  <HiOutlineBell size={20} />
                </span>
              </div>
            </Button>
          );
        }

        return (
          <Button
            type='button'
            variant='secondary'
            size='base'
            data-bs-toggle='modal'
            data-bs-target='#backInStockSubscribe'
            data-item-id={id}
            data-item-display-name={displayName}>
            Notify Me When In Stock
          </Button>
        );
      } else {
        return (
          <Button
            disabled
            variant='primary'
            size='lg'
            fullWidth='all'
            noPadding
            className='tw:h-[64px] tw:rounded-full tw:px-8 tw:py-2 tw:text-base tw:font-normal tw:normal-case'>
            SOLD OUT
          </Button>
        );
      }
    } else if (isGenericProduct && showPicker) {
      if (isVariant) {
        return (
          <Button
            disabled
            fullWidth='all'
            type='button'
            variant='primary'
            size='lg'
            noPadding
            className='tw:h-[64px] tw:rounded-full tw:px-8 tw:py-2 tw:text-base tw:font-normal tw:normal-case'>
            <div className='tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2'>
              <span>Make a selection to purchase</span>
              <span>
                <HiOutlineShoppingBag size={20} />
              </span>
            </div>
          </Button>
        );
      }

      return (
        <Button type='button' fullWidth='all' size='lg' disabled>
          Make a selection to purchase
        </Button>
      );
    } else if (stock === 'preOrder') {
      if (isVariant) {
        return (
          <Button
            onClick={addToCartSubmit}
            fullWidth='all'
            type='button'
            variant='primary'
            size='lg'
            noPadding
            className='tw:h-[64px] tw:rounded-full tw:px-8 tw:py-2 tw:text-base tw:font-normal tw:normal-case'>
            <div className='tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2'>
              <span>Pre-Order</span>
              <span>
                <HiOutlineShoppingBag size={20} />
              </span>
            </div>
          </Button>
        );
      }

      return (
        <React.Fragment>
          <meta itemProp='availability' content='https://schema.org/PreOrder' />
          <Button type='button' fullWidth='all' variant='primary' size='base' onClick={addToCartSubmit}>
            Pre-Order
          </Button>
        </React.Fragment>
      );
    } else if (checkShopifyId && !shopifyId) {
      trackError('Shopify product not synced', { displayName, id });
      return (
        <Button disabled variant='primary' size='lg'>
          TEMPORARILY UNAVAILABLE
        </Button>
      );
    } else {
      if (isVariant) {
        return (
          <React.Fragment>
            <meta itemProp='availability' content='https://schema.org/InStock' />
            <Button
              onClick={addToCartSubmit}
              fullWidth='all'
              type='button'
              variant='primary'
              size='lg'
              noPadding
              className='tw:h-[64px] tw:rounded-full tw:px-8 tw:py-2 tw:text-base tw:font-normal tw:normal-case'>
              <div className='tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2'>
                <span>
                  Add To Cart
                  {selected?.length > 0 && (
                    <span className='tw:block tw:text-sm tw:font-light tw:normal-case'>
                      Item + {selected.length} {selected.length > 1 ? <>Accessories</> : <>Accessory</>}
                    </span>
                  )}
                </span>
                <span>
                  <HiOutlineShoppingBag size={20} />
                </span>
              </div>
            </Button>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment>
          <meta itemProp='availability' content='https://schema.org/InStock' />
          <Button onClick={addToCartSubmit} fullWidth='all' type='button' variant='primary' size='lg'>
            <div>
              Add to cart
              {selected?.length > 0 && (
                <span className='tw:block tw:text-sm tw:font-light tw:normal-case'>
                  Item + {selected.length} {selected.length > 1 ? <>Accessories</> : <>Accessory</>}
                </span>
              )}
            </div>
          </Button>
        </React.Fragment>
      );
    }
  }, [
    stock,
    isGenericProduct,
    showPicker,
    selected,
    addToCartSubmit,
    klaviyoConfigured,
    isBundle,
    id,
    displayName,
    loading,
    isVariant,
  ]);

  return (
    <div id='addToCart'>
      <div className='checkout-section'>
        <div className='add-to-cart-form'>
          <div className='tw:mb-5 tw:mt-5 tw:flex tw:items-center tw:justify-between tw:gap-2'>
            {renderQuantitySection}
            {renderCTABtn()}
          </div>

          {success && (
            <AddedToCartModal title='Added To Cart' id='AddedToCartModal' relatedItems={relatedItems} />
          )}
        </div>
      </div>
      <DesignExporter
        ref={designExporterRef}
        success={_addToCartSubmit}
        failure={(e) => {
          alert('There was a problem exporting your design.');
          dispatch({
            type: CartItemAddActionTypes.FAIL,
          });
        }}
      />
    </div>
  );
};

AddToCart.defaultProps = {
  addOns: [],
  bundleItems: [],
  relatedItems: [],
};

export default memo(AddToCart);
